import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	AccountCreateModal,
	AccountListTable,
	AccountRemoveModal,
	AccountEditCloneModal,
	UiPagination,
	ToolPanel,
	IconPlus,
	ESortOrder,
	IconDeleteBin,
	UiNotification,
	AccountDeletedListModal
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useAccountList, useFiltersAccountPage, useSortAccountPage, useAccountDeletedList } from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import { IFetchItemsSetup, IAccount, ERoleAccess, ETableAction, ETableRemoveType } from '../types'
import { TAKE, MENU } from '../constants'
import { Access } from '../lib'
import { TopButtonsBox } from './page.RolePage'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

export const AccountPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsCommonToReload = useSetAtom(state.fetchIsCommonToReloadAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const pageTitle = t(MENU.account.title)

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		accountFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useFiltersAccountPage()

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useSortAccountPage()

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useAccountList(setup, send)

	const setup2: IFetchItemsSetup = {
		filter: [],
		sort: [],
		skip: 0,
		take: 0
	}

	const { total: total2, warmUp: warmUp2 } = useAccountDeletedList(setup2, true)

	const [viewDeletedAccounts, setViewDeletedAccounts] = useState<boolean>(false)
	const [createAccount, setCreateAccount] = useState<boolean>(false)
	const [editAccount, setEditAccount] = useState<IAccount | null>(null)
	const [cloneAccount, setCloneAccount] = useState<IAccount | null>(null)
	const [removeAccount, setRemoveAccount] = useState<IAccount | null>(null)
	const [removeAccounts, setRemoveAccounts] = useState<IAccount[] | null>(null)
	const [selectedRows, setSelectedRows] = useState<IAccount[]>([])

	const onViewDeletedAccounts = (e: React.MouseEvent) => {
		e.preventDefault()
		setViewDeletedAccounts(true)
	}
	const onCreate = (e: React.MouseEvent) => {
		e.preventDefault()
		setCreateAccount(true)
	}
	const onEdit = (item: IAccount, e: React.MouseEvent) => {
		e.preventDefault()
		setEditAccount(item)
	}
	const onClone = (item: IAccount, e: React.MouseEvent) => {
		e.preventDefault()
		setCloneAccount(item)
	}
	const onRemove = (item: IAccount, e: React.MouseEvent) => {
		e.preventDefault()
		setRemoveAccount(item)
	}

	const onMultiSelect = (selectedRows: IAccount[]) => {
		setSelectedRows(selectedRows)
	}

	const onMultiDelete = async (e: React.MouseEvent) => {
		e.preventDefault()
		//console.log(`onMultiDelete: selectedRows:: ${selectedRows}`)
		setRemoveAccounts(selectedRows)
	}

	const UnscopedNotification = () => {
		if (total2 <= 0 || !$access.$(ERoleAccess.AccountResurrect)) return null

		const notification = () => {
			// TODO: check translation for plural form or to change words order ...?
			return (
				<>
					<span
						className={'deleted-accounts'}
						onClick={onViewDeletedAccounts}
					>
						{`${t(ALL_LANG_KEY.account_page.notification_deleted_accounts_count, { cnt: total2 })}`}
					</span>
					&nbsp;
					{t(ALL_LANG_KEY.account_page.notification_deleted_accounts_title)}
				</>
			)
		}

		return <UiNotification text={notification()}/>
	}

	return (
		<>
			<PageTitle title={pageTitle}/>

			<div className="section-title mt-25 mb-24 text-center">
				<UnscopedNotification/>
			</div>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/* remove CREATE button for 'account' main page (without pre-selected group) */}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						<TopButtonsBox>
							{
								!!onMultiDelete && !!$access.$(ERoleAccess.AccountRemove) && selectedRows.length > 0 ? (
									(
										<button
											type="button"
											className="btn btn-primary btn-danger d-flex align-items-center"
											onClick={onMultiDelete}
										>
											<IconDeleteBin size={16}/>
											<span className="d-none d-lg-block">
												{`${t(ALL_LANG_KEY.account_page.btn_delete_multiple_items, { cnt: selectedRows.length })}`}
											</span>
										</button>
									)
								) : null
							}

							{
								!!onCreate && !!$access.$(ERoleAccess.AccountCreate)
									? (
										<button
											type="button"
											className="btn btn-primary d-flex align-items-center"
											onClick={onCreate}
										>
											<IconPlus/>
											<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.account_page.btn_add_new)}`}</span>
										</button>
									)
									: null
							}
						</TopButtonsBox>
					</div>

					<Filters
						config={accountFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.AccountPage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<AccountListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onClone={onClone}
						onRemove={onRemove}
						onMultiSelect={onMultiSelect}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!createAccount && !!$access.$(ERoleAccess.AccountCreate) && <AccountCreateModal
					show={createAccount}
					onClose={() => setCreateAccount(false)}
					onSave={() => {
						setCreateAccount(false)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

			{
				!!editAccount && !!$access.$(ERoleAccess.AccountModify) && <AccountEditCloneModal
					action={ETableAction.edit}
					item={editAccount}
					show={!!editAccount}
					onClose={() => setEditAccount(null)}
					onSave={() => {
						setEditAccount(null)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

			{
				!!cloneAccount && !!$access.$(ERoleAccess.AccountModify) && <AccountEditCloneModal
					action={ETableAction.clone}
					item={cloneAccount}
					show={!!cloneAccount}
					onClose={() => setCloneAccount(null)}
					onSave={() => {
						setCloneAccount(null)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

			{
				!!removeAccount && !!$access.$(ERoleAccess.AccountRemove) && <AccountRemoveModal
					type={ETableRemoveType.single}
					item={removeAccount}
					show={!!removeAccount}
					onClose={() => setRemoveAccount(null)}
					onSave={() => {
						setRemoveAccount(null)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

			{
				!!removeAccounts && !!$access.$(ERoleAccess.AccountRemove) && <AccountRemoveModal
					type={ETableRemoveType.multi}
					items={removeAccounts}
					show={!!removeAccounts}
					onClose={() => setRemoveAccounts(null)}
					onSave={() => {
						setRemoveAccounts(null)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

			{
				!!viewDeletedAccounts && !!$access.$(ERoleAccess.AccountResurrect) && <AccountDeletedListModal
					show={!!viewDeletedAccounts}
					take={take}
					onClose={() => {
						setViewDeletedAccounts(false)
						navigate(href(0).tail)
						warmUp()
						warmUp2()
						setFetchIsCommonToReload(state.launchUpdateListAccountBuyer)
					}}
				/>
			}

		</>
	)
}
